import { useCallback, useEffect, useRef, useState } from 'react'
import uuidv4 from 'uuid/v4'

import { axios, axiosPlain } from 'api'
import { useVideoPlayerContext } from './VideoPlayerContext'
import { humanReadableDate } from 'components_v2/Membership/Edit/SubscriptionStates/formatTimes'

const COMPLETE_AT_PERCENT = 79

const camelToSnake = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.replace(/([A-Z])/g, (match) => `_${match.toLowerCase()}`),
      value
    ])
  )
}

const useVideoTracking = ({ setSubscriptionEndTime = () => {} }) => {
  const { state } = useVideoPlayerContext()
  const trialExtend = state.trialExtend

  const [timeupdateEvents, setTimeupdateEvents] = useState([])
  const [additionalData, setAdditionalData] = useState({})
  const fetchRef = useRef()
  const [completed, setCompleted] = useState(false)
  const [triggeredTimeArray, setTriggeredTimeArray] = useState([])

  useEffect(() => {
    setCompleted(false)
    if (state.meta?.videoOriginType) {
      // We need a new client_uuid every post request.
      const client_uuid = uuidv4()
      const identifier =
        state.meta.videoOriginType === 'Plan'
          ? state.meta.planId
          : state.meta.planEntryId
      const postBody = {
        id: identifier,
        video_origin_type: state.meta.videoOriginType,
        occurred_at: new Date().toUTCString(),
        client_uuid
      }

      axiosPlain
        .post('video_events/handle_video_event', postBody)
        .then((res) => {
          if (res.data.ga4_event) {
            window.dataLayer.push(res.data.ga4_event)
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [state.playStarted])

  const getTimeupdateEvents = useCallback(async () => {
    fetchRef.current = true
    try {
      const res = await axios.get('video_events/time_updates', {
        params: {
          version: '1',
          video_id: state.meta.videoId
        }
      })

      if (res && res.data && res.data.timestamps) {
        setTimeupdateEvents(res.data.timestamps)
      }
    } catch (e) {
      // fail silently?
    }
  }, [state.meta])

  const getSuggestion = useCallback(() => {
    const type = state.meta.type
    const uuid = state.meta.uuid
    if (type) {
      if (type === 'series') {
        return { type: 'series' }
      } else if (type === 'playlist') {
        return {
          type: 'playlist',
          uuid
        }
      }
    } else {
      return null
    }
  }, [state.meta])

  /* Get timeupdate tracking events and set up additionalData based on `video` state.meta */
  useEffect(() => {
    if (state.meta && state.meta.videoId && state.meta.planEntryId) {
      if (!fetchRef.current) getTimeupdateEvents() // only get timeupdateEvents once

      setAdditionalData({
        referrer: state.meta.referrer,
        suggestion: getSuggestion()
      })
    }
  }, [state.meta])
  /* Set up manual event listeners once we have timeupdateEvents */
  useEffect(() => {
    if (state.meta) {
      if (
        timeupdateEvents &&
        timeupdateEvents.length > 0 &&
        state.playStarted
      ) {
        if (timeupdateEvents && Array.isArray(timeupdateEvents)) {
          timeupdateEvents.forEach((time) => {
            if (state.time >= time && !triggeredTimeArray.includes(time)) {
              // We need a new client_uuid every post request.
              setTriggeredTimeArray((state) => [...state, time])
              const client_uuid = uuidv4()

              const postBody = {
                plan_entry_id: state.meta.planEntryId,
                progress_seconds: time,
                occurred_at: new Date().toUTCString(),
                client_uuid,
                ...additionalData
              }

              axiosPlain
                .post('video_events/time_update/web', postBody, {
                  params: {
                    version: '1'
                  }
                })
                .then((res) => {
                  if (res.data?.ga4_event) {
                    window.dataLayer.push(res.data.ga4_event)
                  }
                })
                .catch((err) => {
                  console.log(err)
                })
            }
          })
        }

        const progress_percentage =
          ((state.time * 1000.0) / state?.meta?.duration) * 100

        if (!completed && progress_percentage > COMPLETE_AT_PERCENT) {
          setCompleted(true)
          const client_uuid = uuidv4()
          const postBody = {
            plan_entry_id: state.meta.planEntryId,
            progress_seconds: state.time,
            occurred_at: new Date().toUTCString(),
            client_uuid,
            ...additionalData
          }

          axiosPlain
            .post('video_events/time_update/web', postBody, {
              params: {
                version: '1'
              }
            })
            .then((res) => {
              if (res.data?.ga4_event) {
                window.dataLayer.push({
                  ...res.data.ga4_event,
                  event: 'video_complete'
                })
              }
            })
            .catch((err) => {
              console.log(err)
            })
        }
      }
    }
  }, [
    timeupdateEvents,
    state.playStarted,
    state.time,
    state.meta,
    additionalData,
    triggeredTimeArray
  ])

  useEffect(() => {
    if (state.playStarted && trialExtend) {
      axios
        .post('users/extend_trial', camelToSnake(trialExtend))
        .then((resp) => {
          const subscriptionEndTime = resp?.data?.subscriptionEndTime
          if (subscriptionEndTime) {
            const date = new Date(subscriptionEndTime)
            setSubscriptionEndTime(humanReadableDate(date))
          }
        })
        .catch((err) => {
          console.log(err)
        })
    }
  }, [state.playStarted, trialExtend, setSubscriptionEndTime])
}

export default useVideoTracking
